import {
  AccountData,
  AudienceList,
  FollowerStatus,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../../redux/slices/accountSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import filter from "lodash.filter";
import { Colors } from "../../../../../utils/colors";
import { MassTextsPersonItem } from "../../Items/MassTextsPersonItem";
import { EmptySearchState } from "../../../../EmptyStates/EmptySearchState";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import { MassTextsActionItem } from "../../Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import { SelectRecipientsSharedProps } from "../SelectRecipientsScreens";
import {
  addOrFilterRecipients,
  filterFollowersByStatus,
  handlePluralString,
  textableContactString,
} from "@markit/common.utils";
import { useLiveUpdatingListAnalytics } from "../../../../../hooks/useLiveHooks/useLiveUpdatingListAnalytics";
import { useLoadAudienceListMembers } from "../../../../../hooks/useLoadAudienceListMembers";
import { HorizontalDivider } from "../../../../Dividers/HorizontalDivider";
import VirtuosoList from "../../../../Lists/VirtuosoList";

type SelectRecipientsAudienceListProps = SelectRecipientsSharedProps & {
  selectedList: AudienceList;
};

const SelectRecipientsAudienceList = (
  props: SelectRecipientsAudienceListProps
) => {
  const {
    selectedList,
    unsavedRecipients,
    setUnsavedRecipients,
    excludingMode,
    setProfileSelected,
    displayAllFollowers,
  } = props;
  const { account } = useSelector(getAccountState);
  const { followersData } = account;
  const [searchTerm, setSearchTerm] = useState("");
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const {
    liveListContactUids,
    totalMembers,
    loaded,
    allUidsLoaded,
    loadMoreUids,
    loadAllUids,
  } = useLiveUpdatingListAnalytics({
    listId: selectedList.id,
  });

  const visibleFollowers = useMemo(
    () =>
      displayAllFollowers
        ? liveListContactUids
        : filterFollowersByStatus(
            liveListContactUids,
            followersData,
            FollowerStatus.SUBSCRIBED
          ),
    [displayAllFollowers, followersData, liveListContactUids]
  );

  const {
    isFinished,
    isLoading,
    fetchedUserData,
    loadMoreUsers,
    loadSearchResultsThrottled,
  } = useLoadAudienceListMembers({
    userIdList: loaded ? visibleFollowers : [],
    windowSize: 20,
  });

  const iconColor = useMemo(
    () => (excludingMode ? Colors.RED3 : Colors.BLUE5),
    [excludingMode]
  );

  useEffect(() => {
    if (loaded) {
      loadMoreUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  // When account data runs out but more UID pages exist, fetch the next UID page
  const handleLoadMore = useCallback(async () => {
    if (!isFinished) {
      await loadMoreUsers();
    } else if (!allUidsLoaded) {
      await loadMoreUids();
    }
  }, [isFinished, allUidsLoaded, loadMoreUsers, loadMoreUids]);

  const handleSelectAll = useCallback(async () => {
    let allUids = visibleFollowers;
    if (!allUidsLoaded) {
      setIsSelectingAll(true);
      const fetchedAll = await loadAllUids();
      setIsSelectingAll(false);
      allUids = displayAllFollowers
        ? fetchedAll
        : filterFollowersByStatus(
            fetchedAll,
            followersData,
            FollowerStatus.SUBSCRIBED
          );
    }
    addOrFilterRecipients(unsavedRecipients, setUnsavedRecipients, allUids);
  }, [
    allUidsLoaded,
    displayAllFollowers,
    followersData,
    loadAllUids,
    setUnsavedRecipients,
    unsavedRecipients,
    visibleFollowers,
  ]);

  const containsUser = useCallback((user: AccountData, query: string) => {
    return user.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    if (text !== "") {
      loadSearchResultsThrottled(text.toLowerCase());
    }
    setSearchTerm(text);
  };

  const membersToShow: AccountData[] = useMemo(() => {
    let searchResults: AccountData[] = fetchedUserData;
    if (searchTerm !== "") {
      searchResults = filter(fetchedUserData, (user: AccountData) => {
        return containsUser(user, searchTerm);
      });
    }
    return searchResults;
  }, [fetchedUserData, searchTerm, containsUser]);

  const isAllSelected = useCallback(
    (totalList: string[], subList: string[]) => {
      if (subList.length === 0) return false;
      const subSet = new Set(subList);
      return totalList.every((sel) => subSet.has(sel));
    },
    []
  );

  const renderHeader = useMemo(() => {
    return (
      <div>
        <MassTextsActionItem
          title={"Select All"}
          icon={
            <Icon icon="ion:people" height={32} style={{ color: iconColor }} />
          }
          onPress={handleSelectAll}
          subtext={textableContactString(totalMembers)}
          isCheckSelected={isAllSelected(visibleFollowers, unsavedRecipients)}
          disabled={membersToShow.length === 0}
          loading={!loaded || isSelectingAll}
          checkColor={iconColor}
        />
        <HorizontalDivider altMargin={10} />
      </div>
    );
  }, [
    iconColor,
    handleSelectAll,
    totalMembers,
    isAllSelected,
    visibleFollowers,
    unsavedRecipients,
    membersToShow.length,
    loaded,
    isSelectingAll,
  ]);

  return (
    <>
      <div
        className="ColumnCenter"
        style={{
          gap: 4,
          backgroundColor: Colors.GRAY6,
          padding: 10,
          borderRadius: 12,
        }}
      >
        <span style={{ fontWeight: 500, fontSize: 14 }}>
          {selectedList.name}
        </span>
        <span style={{ fontSize: 12, color: Colors.GRAY1 }}>
          {`${loaded ? totalMembers : "--"} ${handlePluralString(
            "Contact",
            totalMembers
          )}`}
        </span>
      </div>
      <SearchBoxContainer
        value={searchTerm}
        placeholder="Search People"
        onChange={(e) => handleSearch(e.target.value)}
        containerStyles={{ marginBottom: 10 }}
      />
      <VirtuosoList
        items={membersToShow}
        loadMoreOnPress={handleLoadMore}
        renderItem={(item) => {
          return (
            <MassTextsPersonItem
              user={item}
              unsavedPeople={unsavedRecipients}
              setUnsavedPeople={setUnsavedRecipients}
              excludingMode={excludingMode}
              setProfileSelected={setProfileSelected}
            />
          );
        }}
        isLoading={isLoading || !loaded}
        isFinished={isFinished && allUidsLoaded}
        searchTerm={searchTerm}
        renderNonSearchEmptyView={
          <EmptySearchState mainText="No people to show" />
        }
        renderHeader={renderHeader}
      />
    </>
  );
};

export default SelectRecipientsAudienceList;
